import React from 'react';
import { CheckCircle, Award, Users, Clock } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <section
        className="relative h-[400px] flex items-center justify-center"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1764885449418-db6632922a19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob3NwaXRhbCUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NTc1NTE4OXww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/80 to-[#0A2540]/60" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="about-heading"
          >
            About Aaric Medical Center
          </h1>
          <p className="text-xl text-white/90">AMC for your health — unifying all healthcare services under one center</p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg mb-8">
            <h2
              className="text-3xl font-bold text-[#0A2540] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Our Mission & Vision
            </h2>
            <p className="text-[#475569] text-lg leading-relaxed mb-4">
              At Aaric Medical Center, we are on a mission to unify all fragmented healthcare services under one center. Whether you need diagnostics, pharmacy, specialist consultations, or emergency care — AMC brings it all together, available both online and at our facility.
            </p>
            <p className="text-[#475569] text-lg leading-relaxed">
              Our vision is to be the most trusted healthcare provider, known for our patient-centric approach, advanced medical infrastructure, and unwavering commitment to making quality healthcare accessible to everyone.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="bg-[#0EA5E9]/10 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                <Clock className="h-8 w-8 text-[#0EA5E9]" />
              </div>
              <h3 className="text-2xl font-semibold text-[#0A2540] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                24/7 Availability
              </h3>
              <p className="text-[#475569] leading-relaxed">
                We understand that medical emergencies don't follow a schedule. That's why our doors are always open, providing round-the-clock emergency services, diagnostic facilities, and expert medical care.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="bg-[#0EA5E9]/10 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-[#0EA5E9]" />
              </div>
              <h3 className="text-2xl font-semibold text-[#0A2540] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Expert Medical Team
              </h3>
              <p className="text-[#475569] leading-relaxed">
                Our team of highly qualified doctors, nurses, and healthcare professionals bring years of experience and dedication to patient care. We believe in treating every patient like family.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <h2
              className="text-3xl font-bold text-[#0A2540] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Advanced Infrastructure
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-[#0EA5E9] mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-[#0A2540] mb-1">State-of-the-art Diagnostic Equipment</h4>
                  <p className="text-[#475569] text-sm">Advanced ultrasound, digital X-ray, ECG, and echo-cardiogram facilities</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-[#0EA5E9] mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-[#0A2540] mb-1">Modern Laboratory</h4>
                  <p className="text-[#475569] text-sm">Comprehensive blood testing and pathology services</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-[#0EA5E9] mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-[#0A2540] mb-1">In-house Pharmacy</h4>
                  <p className="text-[#475569] text-sm">Well-stocked pharmacy with online ordering options</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-[#0EA5E9] mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-[#0A2540] mb-1">Emergency Care Unit</h4>
                  <p className="text-[#475569] text-sm">Fully equipped emergency department for critical care</p>
                </div>
              </div>
            </div>

            <div className="bg-[#0EA5E9]/5 rounded-xl p-6 border-l-4 border-[#0EA5E9]">
              <div className="flex items-start">
                <Award className="h-8 w-8 text-[#0EA5E9] mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-[#0A2540] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Path to NABL M(EL)T Accreditation
                  </h3>
                  <p className="text-[#475569] leading-relaxed">
                    We are committed to maintaining the highest standards of quality and accuracy. Our laboratory is on the path towards National Accreditation Board for Testing and Calibration Laboratories (NABL) M(EL)T accreditation, ensuring reliable and accurate diagnostic results for our patients.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;